import { reactive, watch, ref } from '@vue/composition-api';
import store from '@/store';

import usePagination from '@/views/components/pagination/usePagination';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';
import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';
import useDate from '@/utils/hooks/useDate';

import { setBannerModel, groupBannerModel } from '@/models/banner/banner';

export default function useAssignBannerModal(props) {
  const {
    fetchAssignSetBannerListModel,
    assignToSetBannerModel,
  } = setBannerModel();

  const keyChange = ref(0);

  const {
    fetchAssignGroupBannerListModel,
    fetchAssignSetToGroupBannerListModel,
    assignToGroupBannerModel,
    assignSetToGroupBannerModel,
  } = groupBannerModel();

  const { pagination } = usePagination();

  const { openDefaultErrorModal } = useValidationModal();

  const { filters, searchValue, onSearch } = useFilterHeaderTable();

  const { resolveDateWithSeperator } = useDate();

  const setBannerTable = reactive({
    payload: [],
    rows: 0,
  });

  const groupBannerTable = reactive({
    payload: [],
    rows: 0,
  });

  const loading = reactive({ tableData: false, api: true });

  const bannerGroupField = [
    { key: 'seq', label: 'No.' },
    { key: 'group_name', label: 'Group Name' },
    { key: 'valid_from', label: 'Valid From' },
    { key: 'valid_through', label: 'Valid Through' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
  ];

  const bannerSetField = [
    { key: 'seq', label: 'No.' },
    { key: 'set_name', label: 'Set Name' },
    { key: 'valid_from', label: 'Valid From' },
    { key: 'valid_through', label: 'Valid Through' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '' },
  ];

  const fetchSetBannerList = (loadingAction = true) => {
    fetchAssignSetBannerListModel.payload.Banner_Id = props.bannerId;
    fetchAssignSetBannerListModel.payload.Page = pagination.currentPage;
    fetchAssignSetBannerListModel.payload.Limit = pagination.perPage;
    fetchAssignSetBannerListModel.payload.Filters = filters;
    loading.api = true;

    if (loadingAction) {
      loading.tableData = true;
    }

    store
      .dispatch('api/apiRequest', fetchAssignSetBannerListModel)
      .then((response) => {
        setBannerTable.payload = response.data.payload;
        setBannerTable.rows = response.data.payload.length;
        pagination.rows = setBannerTable.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
        loading.api = false;
      });
  };

  const fetchGroupBannerList = (loadingAction = true) => {
    fetchAssignGroupBannerListModel.payload.Banner_Id = props?.bannerId;
    fetchAssignGroupBannerListModel.payload.Page = pagination.currentPage;
    fetchAssignGroupBannerListModel.payload.Limit = pagination.perPage;
    fetchAssignGroupBannerListModel.payload.Filters = filters;
    loading.api = true;

    if (loadingAction) {
      loading.tableData = true;
    }

    store
      .dispatch('api/apiRequest', fetchAssignGroupBannerListModel)
      .then((response) => {
        groupBannerTable.payload = response.data.payload;
        groupBannerTable.rows = response.data.payload.length;
        pagination.rows = groupBannerTable.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
        loading.api = false;
      });
  };

  const fetchSetBannerGroupBannerList = (loadingAction = true) => {
    fetchAssignSetToGroupBannerListModel.payload.Set_Banner_Id =
      props?.bannerId;
    fetchAssignSetToGroupBannerListModel.payload.Page = pagination.currentPage;
    fetchAssignSetToGroupBannerListModel.payload.Limit = pagination.perPage;
    fetchAssignSetToGroupBannerListModel.payload.Filters = filters;

    loading.api = true;

    if (loadingAction) {
      loading.tableData = true;
    }

    store
      .dispatch('api/apiRequest', fetchAssignSetToGroupBannerListModel)
      .then((response) => {
        groupBannerTable.payload = response.data.payload;
        groupBannerTable.rows = response.data.payload.length;
        pagination.rows = groupBannerTable.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
        loading.api = false;
      });
  };

  const assignToSetBanner = (banner) => {
    banner.assigned = true;
    keyChange.value++;
    assignToSetBannerModel.payload.Banner_Id = props?.bannerId;
    assignToSetBannerModel.payload.Set_Banner_Id = banner.set_banner_id;

    store
      .dispatch('api/apiRequest', assignToSetBannerModel)
      .then((response) => {
        setBannerTable.rows--;

        if (setBannerTable.rows <= 0) {
          return pagination.currentPage--;
        }

        fetchSetBannerList(false);
      })
      .catch((err) => {
        banner.assigned = false;
        keyChange.value++;
        openDefaultErrorModal(err);
      });
  };

  const assignToGroupBanner = (banner) => {
    banner.assigned = true;
    keyChange.value++;
    assignToGroupBannerModel.payload.Banner_Id = props?.bannerId;
    assignToGroupBannerModel.payload.Group_Banner_Id = banner.group_banner_id;

    store
      .dispatch('api/apiRequest', assignToGroupBannerModel)
      .then((response) => {
        groupBannerTable.rows--;

        if (groupBannerTable.rows <= 0) {
          return pagination.currentPage--;
        }

        fetchGroupBannerList(false);
      })
      .catch((err) => {
        banner.assigned = false;
        keyChange.value++;
        openDefaultErrorModal(err);
      });
  };

  const assignSetToGroupBanner = (banner) => {
    banner.assigned = true;
    keyChange.value++;
    assignSetToGroupBannerModel.payload.Set_Banner_Id = props?.bannerId;
    assignSetToGroupBannerModel.payload.Group_Banner_Id =
      banner.group_banner_id;

    store
      .dispatch('api/apiRequest', assignSetToGroupBannerModel)
      .then((response) => {
        groupBannerTable.rows--;

        if (groupBannerTable.rows <= 0) {
          return pagination.currentPage--;
        }

        fetchSetBannerGroupBannerList(false);
      })
      .catch((err) => {
        banner.assigned = false;
        keyChange.value++;
        openDefaultErrorModal(err);
      });
  };

  const onOpenModal = () => {
    searchValue.query = null;

    if (filters.value.length > 0) {
      return (filters.value = []);
    }

    pagination.currentPage = 1;
    fetchModalData();
  };

  const resolveBannerStatusLabel = (status) => {
    if (status) {
      return 'Active';
    }

    return 'Inactive';
  };

  const fetchModalData = () => {
    if (props?.isSetBanner) {
      return fetchSetBannerGroupBannerList();
    }

    if (props?.isSet) {
      return fetchSetBannerList();
    }

    return fetchGroupBannerList();
  };

  const onClickAssignButton = (banner) => {
    if (props?.isSetBanner) {
      return assignSetToGroupBanner(banner);
    }

    if (props?.isSet) {
      return assignToSetBanner(banner);
    }

    return assignToGroupBanner(banner);
  };

  const onSearchBanner = (searchQuery) => {
    searchValue.query = searchQuery;
    onSearch();
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  watch(
    filters,
    () => {
      if (pagination.currentPage !== 1) {
        return (pagination.currentPage = 1);
      }

      fetchModalData();
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.api) {
        fetchModalData();
      }
    },
    { deep: true }
  );

  return {
    bannerGroupField,
    bannerSetField,
    setBannerTable,
    groupBannerTable,
    pagination,
    keyChange,
    loading,
    onOpenModal,
    resolveBannerStatusLabel,
    onClickAssignButton,
    onSearchBanner,
    onPageChange,
    resolveDateWithSeperator,
  };
}
